// M+3.6 C1 — theme derivation + contrast audit. Single source of truth used
// by both the live editor preview and runtime theme application in App.jsx.
//
// The editable surface and the derived one are declared, not described:
// `src/lib/themeVars.mjs` and `DERIVED_VARS` below. The counts that stood here
// were four tokens stale before 135 read them.

/* ── 135 §1(b) — THE TWO LISTS MOVED, AND THEY MOVED FOR A REASON ───────────
 *
 * `EDITABLE_VARS` and `OVERRIDABLE_DERIVED_VARS` were declared here and copied
 * BY HAND into `server/src/routes/themes.js`'s `ALLOWED_VAR_KEYS`. The copy
 * fell behind twice — Phase 8 Stage 3's four tokens, and 132 §7's `--nwd` —
 * and the second time cost Laurent a setting that saved and did not stick.
 *
 * They now live in `src/lib/themeVars.mjs`, which BOTH PLANES read: the client
 * through this re-export, the server through `require`. That file's header
 * carries the argument for deriving rather than checking, the `.mjs` precedent,
 * and the six-hex constraint on what may ever join them.
 *
 * RE-EXPORTED RATHER THAN RE-IMPORTED AT EVERY CALL SITE: seven files import
 * these names from `themeDerivation`, and moving a declaration is not a licence
 * to rewrite seven imports (rule 3). The names, their order and their values
 * are unchanged — the only difference is which file the array literal sits in.
 */
export { EDITABLE_VARS, OVERRIDABLE_DERIVED_VARS, STORABLE_VARS } from "./themeVars.mjs";

export const DERIVED_VARS = [
  "--ts",
  "--tm",
  "--as",
  "--ag",
  "--ps",
  "--gs",
  "--ams",
  "--rs",
  "--hlj",
  "--hll",
  "--nwd",
  "--shd",
  // 164 §A.7 — stated-amount text, from the accent (see deriveThemeVars).
  "--st",
];

// THE THEME EDITOR'S VARIABLE VOCABULARY. `varLabel(k)` is reached with a
// VARIABLE — the editor iterates its groups — so the key is computed, which is
// the family this tree already carries for `permView.*` and
// `caseTeamRights.bit_*`. The English stays here as the dictionary's fallback:
// it is what an unseeded box renders, and it must equal the seeded `en` row
// (the divergence class brief 40's §6 walk found).
const VAR_LABEL_EN: Record<string, string> = {
  "--bd": "Page background",
  "--bs": "Surface background",
  "--bc": "Card background",
  "--bh": "Row hover background",
  "--bi": "Input background",
  "--br": "Border",
  "--tp": "Primary text",
  "--ct": "Contrast text",
  "--ac": "Accent / Selection",
  "--pp": "Secondary accent",
  "--or": "Orange / archive",
  /* 203-glance-2 §B [RULED — Laurent, 2026-09-21: "baptise it « main color »
     in the settings appearance pane"] — the token the firm's name and every
     document's title wear. Seeded under `theme.var.brand2` (VAR_LABEL_KEY_OVERRIDE
     below: a changed string is a new key), the old « Brand colour » rows inert.
     204 §A.4 [RULED — Laurent, 2026-09-22] — AND « MAIN COLOUR » MOVED OUT OF
     THIS EDITOR: the firm's main colour is ONE value, the DAY theme's `--brand`,
     and it is edited under Branding (SettingsView's FirmMainColourField, wearing
     `theme.var.brand2`). A theme's own `--brand` stays editable here because
     its weekend tint and its shadows derive from it (deriveThemeVars below), but
     on a personal or night theme it is not the firm's colour, so it cannot be
     called that. Seeded under `theme.var.brand3` — the third key for this row,
     for the same reason as the second: the seed only inserts. The words are the
     brief's proposal; Laurent overrules them at the glance. */
  "--brand": "Theme hue",
  "--gn": "Green / success",
  "--am": "Amber / warning",
  "--rd": "Red / danger",
  "--gnm": "Green — muted (available)",
  "--rdm": "Red — muted (borrowed)",
  "--cb": "Collaborator badge",
  "--bf": "Badge fallback text",
  "--ts": "Secondary text",
  "--tm": "Muted text",
  "--hlj": "Jump highlight",
  "--hll": "Link highlight",
  "--nwd": "Non-working day field",
  "--shd": "Drop shadow",
  // 164 §A.7 — the stated-amount text colour.
  "--st": "Stated amount text",
  // Alpha-shade derivations — used as taxonomy-pill backgrounds.
  "--as": "Accent — soft bg",
  "--ag": "Accent — soft hover",
  "--ps": "Violet — soft bg",
  "--gs": "Green — soft bg",
  "--ams": "Amber — soft bg",
  "--rs": "Red — soft bg",
};

/* ── BRIEF 134 §4(a) — WHAT EACH OVERRIDABLE ROW IS DERIVED FROM ────────────
 *
 * Laurent asked to "know what it is derived from". Until now the editor said it
 * ONCE, for the whole group, in a shared paragraph — "text tones from primary
 * text and the surface, the jump highlight from amber, the link highlight from
 * the secondary accent" — which named four of the five and could never name the
 * fifth: `--nwd` joined the group in 132 §7 and the sentence was not rewritten.
 * A shared sentence that enumerates its members is a census with an expiry date
 * (130-A's ruling, in a second place), so it is replaced by a sentence PER ROW
 * and the shared one stops enumerating.
 *
 * THE SENTENCES ARE READ OFF `deriveThemeVars` BELOW, not remembered, and each
 * one says WHAT MOVES IF THE BASE MOVES — because the reader of this line is
 * deciding whether to override, and the thing he is giving up is exactly that
 * following. Where a derivation has more than one input, it says so rather than
 * naming the first: `--ts` and `--tm` take BOTH `--tp` and `--bs`, and `--bs`
 * decides the coefficient as well as the destination.
 *
 * Same shape as VAR_LABEL_EN above and for the same reason — the key is
 * computed, so the English lives beside the builder as the dictionary's
 * fallback, and it must equal the seeded `en` row. */
const VAR_SOURCE_EN: Record<string, string> = {
  "--ts":
    "Primary text mixed toward the surface — 30% of the way on a dark surface, 20% on a light one. Both are bases: change the primary text or the surface and this tone moves with them.",
  "--tm":
    "Primary text mixed further toward the surface than the secondary tone — 55% of the way on a dark surface, 35% on a light one. Both are bases: change the primary text or the surface and this tone moves with them.",
  "--hlj":
    "The theme's amber, exactly as it is. Change the amber and this changes with it — the amber itself is not repointed, because it also carries every warning in the app.",
  "--hll":
    "The theme's secondary accent, exactly as it is. Change that accent and this changes with it — the accent itself is not repointed, because it also carries the violet washes.",
  "--nwd":
    "The surface, moved 6% toward the brand on a dark theme and away from it on a light one. The surface decides the direction and the brand decides the hue, so changing either moves this.",
  /* 203-glance-2 §B — the sentence follows the ruling: the firm's name in
     the sidebar and every exported document's title wear this colour now, and
     a person choosing it should read that here, not discover it. Keyed
     `theme.varsrc.brand2` through VAR_SOURCE_KEY_OVERRIDE (135 §2b: a changed
     string is a new key; the old rows stay seeded and unread). */
  /* 204 §A.4 [JUDGEMENT — advisor, overrulable, 3 rows] — the sentence follows
     the row's new name. It said the firm's name and the documents wear THIS
     value, which stopped being true the moment `--firm-brand` took the DAY
     theme's brand alone (App.tsx's theme effect): on a personal theme this row
     tints the weekends and the shadows and nothing else. It now says that, and
     points at where the firm's colour is set. Keyed `theme.varsrc.brand3`. */
  "--brand":
    "This theme's own hue. It starts as the accent, so leaving it alone changes nothing. Weekends and holidays are tinted with it, and menus and dialogs drop a shadow that carries it. The firm's main colour — the one the firm's name and the title of every exported document wear — is the Day theme's value, set under Branding.",
  // 167 R15 — the sentence corrected (the mix has been toward WHITE on both
  // themes since 164's glance correction, deriveThemeVars below) and widened
  // to what the token marks since 166 R2. Keyed `theme.varsrc.st2` through
  // VAR_SOURCE_KEY_OVERRIDE: a changed sentence is a new key (135 §2b).
  "--st":
    "The accent pulled toward white — 10% of the way on a light theme, 55% on a dark one — so it reads as ice on both. It marks a value the app did not produce itself: a stated amount, a stated hourly rate, an imported code. Change the accent and it moves with it.",
};

/** `theme.var.bd` for `--bd`. The code is the var name without its dashes, so
 *  the key set is derived from EDITABLE_VARS rather than typed twice. */
export const varLabelKey = (k: string) => VAR_LABEL_KEY_OVERRIDE[k] || `theme.var.${k.replace(/^--/, "")}`;

/** `theme.varsrc.ts` for `--ts`. 134 §4(a) — the sibling of `varLabelKey`, and
 *  a SEPARATE key family rather than a suffix on the label's, because a label
 *  is two words and a source is a sentence: they are translated by different
 *  judgements and one of them is much more likely to need revising. */
export const varSourceKey = (k: string) =>
  VAR_SOURCE_KEY_OVERRIDE[k] || `theme.varsrc.${k.replace(/^--/, "")}`;

/** 135 §2(b)/§4 — A CHANGED STRING NEEDS A NEW KEY, and this is where the new
 *  one is named.
 *
 *  `--nwd`'s sentence used to describe a mix toward white or black; §2 makes it
 *  a mix toward the BRAND, so the words had to change. The translation seed
 *  only INSERTS (105's rule), so editing `theme.varsrc.nwd` would leave every
 *  already-seeded instance rendering the old description of an arithmetic that
 *  no longer runs — the worst of both, because the fallback in this file would
 *  be right and the screen would be wrong.
 *
 *  A MAP RATHER THAN A RENAMED DERIVATION: `varSourceKey` stays the rule and
 *  this is the exception list, so the next token added needs no thought and the
 *  one token that has moved says so out loud. The old rows stay seeded and
 *  unread (D28).
 *
 *  `--st` (167 R15): the seeded sentence said "toward white on a dark theme,
 *  toward black on a light one", which 164's glance correction had already
 *  made false (it is toward white on both, deriveThemeVars), and it named only
 *  a stated amount where 166 R2 made the token mark every value the app did
 *  not produce itself. Same rule, same mechanism: `st2`, old rows inert. The
 *  LABEL (`theme.var.st`, "Stated amount text") is left: censused readers are
 *  the editor rows, which carry this sentence beneath it, the contrast-audit
 *  pairs and the ColorOrVarPicker list, where it is the token's NAME — a
 *  handle, not a description. Overturnable at the glance for one key + 3 rows. */
const VAR_SOURCE_KEY_OVERRIDE: Record<string, string> = {
  "--nwd": "theme.varsrc.nwd2",
  "--st": "theme.varsrc.st2",
  "--brand": "theme.varsrc.brand3", // 203-glance-2 §B named the firm name and the documents; 204 §A.4 — those wear the DAY theme's value alone now, the sentence says so
};
/** 203-glance-2 §B — THE LABEL'S OWN EXCEPTION LIST, the same mechanism for the
 *  same reason: « Brand colour » became « Main colour » [RULED], the seed only
 *  inserts, so `theme.var.brand` stays seeded with the old words and a NEW key
 *  carries the new ones. `varLabelKey` stays the rule; this names the one
 *  token that moved. */
const VAR_LABEL_KEY_OVERRIDE: Record<string, string> = {
  "--brand": "theme.var.brand3", // 204 §A.4 — « Main colour » (brand2) belongs to the Branding swatch now; the editor's row is « Theme hue »
};

/** The resolver, as this module needs it. Written out rather than imported so
 *  `themeDerivation` stays free of the i18n module it is used BY — and typed
 *  rather than `any`, because a new `any` gets a type and not a re-recorded
 *  ratchet (SESSION_RULES). */
type Translate = (key: string, vars?: Record<string, unknown>) => string;

/** This module is not a component, so the resolver is passed in. Called
 *  WITHOUT one it still answers — the English, exactly as it always did. */
export const varLabel = (k: string, t?: Translate) =>
  t
    ? t(varLabelKey(k), { fallback: VAR_LABEL_EN[k] || k })
    : VAR_LABEL_EN[k] || k;

/** 134 §4(a) — the sentence under a var's name in the editor, or `""`.
 *
 *  EMPTY, not the key and not the var name, for a var that has none: the row
 *  renders nothing rather than a line of debris.
 *
 *  135 §2 — IT IS NO LONGER ONLY THE OVERRIDABLE ROWS. This comment used to
 *  name them and count them, which is the census-with-an-expiry-date 134 §4
 *  itself ruled against — and `--brand` made the count wrong in the very next
 *  brief. What has one is what `VAR_SOURCE_EN` carries; the map is the list. */
export const varSource = (k: string, t?: Translate) => {
  const en = VAR_SOURCE_EN[k];
  if (!en) return "";
  return t ? t(varSourceKey(k), { fallback: en }) : en;
};

// Canonical :root baseline (mirrors the :root block in App.css). Used as the
// picker fallback for any EDITABLE_VARS key a theme row doesn't carry yet —
// older theme rows predate --ct/--or/--cb/--bf, so without this they'd seed
// the editor at a meaningless hardcoded grey. --bf is the hex equivalent of
// the App.css rgba(200,210,220,.7); the editor is hex-only.
const VAR_DEFAULTS: Record<string, string> = {
  "--bd": "#141820",
  "--bs": "#141820",
  "--bc": "#1a1f2b",
  "--bh": "#1f2537",
  "--bi": "#161b26",
  "--br": "#252d3a",
  "--tp": "#e8ecf4",
  "--ct": "#e0e4ea",
  "--ac": "#4a7cff",
  "--pp": "#a78bfa",
  "--or": "#fb923c",
  "--gn": "#34d399",
  "--am": "#fbbf24",
  "--rd": "#f87171",
  // Phase 16 — muted-pair defaults. Pulled toward ~70% saturation of
  // the vibrant siblings so PAPER checkboxes signal borrow state
  // without screaming.
  "--gnm": "#5a9d80",
  "--rdm": "#cf7a7a",
  "--cb": "#3a4358",
  "--bf": "#c8d2dc",
  // 111 §5d — the two highlight tokens' baseline (== --am / --pp defaults).
  "--hlj": "#fbbf24",
  "--hll": "#a78bfa",
  /* 135 §2(a) — the brand's baseline IS the accent's, deliberately. This map is
     the LAST-RESORT fallback, reached only when `deriveThemeVars` has not been
     consulted; the editor prefers the derived value, which tracks the theme's
     OWN accent rather than this constant. Both roads lead to "today's hue". */
  "--brand": "#4a7cff",
  // 164 §A.7 — mix(--ac #4a7cff, #ffffff, .55): the dark-surface derivation
  // at the default accent, as the picker's last-resort fallback.
  "--st": "#aec4ff",
};
export const varDefault = (k: string) => VAR_DEFAULTS[k] || "#888888";

const VAR_GROUPS = [
  {
    heading: "Surface",
    vars: ["--bd", "--bs", "--bc", "--bh", "--bi", "--br"],
  },
  { heading: "Text", vars: ["--tp", "--ct"] },
  { heading: "Accents", vars: ["--brand", "--ac", "--pp", "--or"] },
  { heading: "Status", vars: ["--gn", "--am", "--rd", "--gnm", "--rdm"] },
  { heading: "Badges", vars: ["--cb", "--bf"] },
];
export const varGroups = () => VAR_GROUPS;

const _hexToRgb = (hex: string) => {
  if (!hex || typeof hex !== "string") return null;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

const _toHex = (n: number) =>
  Math.round(Math.max(0, Math.min(255, n)))
    .toString(16)
    .padStart(2, "0");
const _rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }) =>
  `#${_toHex(r)}${_toHex(g)}${_toHex(b)}`;

// Linear sRGB mix. t=0 → a, t=1 → b. Used for --ts (t=.30) and --tm (t=.55)
// which shift --tp toward --bs by the given amount.
const _mixHex = (a: string, b: string, t: number) => {
  const ra = _hexToRgb(a);
  const rb = _hexToRgb(b);
  if (!ra || !rb) return a || "#000000";
  return _rgbToHex({
    r: ra.r + (rb.r - ra.r) * t,
    g: ra.g + (rb.g - ra.g) * t,
    b: ra.b + (rb.b - ra.b) * t,
  });
};

const _alphaShade = (hex: string, alpha: number) => {
  const rgb = _hexToRgb(hex);
  if (!rgb) return `rgba(0,0,0,${alpha})`;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
};

// M+3.6 C9 — pull --ts / --tm toward the surface (`--bs`), with per-
// luminance coefficients. Original (pre-c7/c8) rule was `mix(--tp, --bs, t)`
// at fixed t=0.30/0.55 — correct direction (muted = less contrast than
// primary against the surface) but too aggressive on light surfaces, where
// perceptual contrast falls off faster and `--bh`/`--bc` sit only a little
// below `--bs`. Light surfaces use a smaller t so derived muted text stays
// dark enough to clear AA-large 3:1 against the subtle hover/card bgs.
//
// Per-theme override: if the editable map already carries `--ts` or `--tm`
// (admin tuning via the theme editor), use the stored value verbatim and
// skip derivation. Other derived vars stay non-overridable.
const _COEFFS_DARK_SURFACE = { ts: 0.3, tm: 0.55 }; // surface is dark
const _COEFFS_LIGHT_SURFACE = { ts: 0.2, tm: 0.35 }; // surface is light

/* ── 135 §2 — HOW FAR BLACK AND WHITE MOVE TOWARD THE BRAND ────────────────
 *
 * RULED (Laurent, 25 Aug 2026): the two shades that reach for literals "should
 * not derive from and towards black, but from and towards the logo colour".
 * `--nwd` and `--shd` were the only two derivations in this file reaching for
 * `#000000` / `#ffffff`; every sibling (`--as`, `--ag`, `--ps`, `--gs`,
 * `--ams`, `--rs`) already tints from a real token, so the ruling is the
 * file's own pattern applied to its last two exceptions.
 *
 * TWO WEIGHTS, NOT ONE, and they differ by an order of magnitude because the
 * two shades reach their pole by completely different routes. Collapsing them
 * into one constant would look tidier and would break one of them.
 *
 * `--nwd` mixes the SURFACE only 6% of the way toward its pole, so whatever
 * hue the pole carries is diluted sixteen-fold before anything is painted. A
 * 6% tint here would leave 0.36% of the brand in the result — arithmetically
 * present, visually nothing, and the ruling defeated. MEASURED at 0.55 across
 * six brands on both surfaces: the direction still holds everywhere (darker on
 * a light theme, lighter on a dark one — 132 §7's whole point), and the
 * results are visibly distinct per brand: on the dark surface blue gives
 * `#1c222d`, amber `#222426`, green `#1b242a`, against today's neutral
 * `#22262d`.
 *
 * `--shd` is painted DIRECTLY, at alpha 0.6, onto a near-black page. Its
 * budget is therefore tiny, and the ceiling is not taste — it is the
 * difference between a shadow and a glow. MEASURED over `--bd #0e1117` at
 * alpha 0.6, across eight brands including saturated extremes: at 0.06 every
 * brand keeps the composite DARKER than the background it falls on (worst
 * case amber, 0.00419 against the background's 0.00556); at 0.08 amber reaches
 * 0.00514 and the margin is gone; by 0.15 every brand LIGHTENS the page, which
 * is a coloured haze under every menu and is exactly what §2(c) says to stop
 * and report rather than ship.
 *
 * The hue survives that budget: on the dark theme a blue brand takes the
 * shadow's darkest point from rgb(6,7,9) to rgb(8,11,18) — the blue channel
 * doubles — so it reads as a cool shadow rather than a neutral one, while
 * still being a shadow.
 */
const _NWD_BRAND_TINT = 0.55;
const _SHADOW_BRAND_TINT = 0.06;

export function deriveThemeVars(editable: Record<string, string> | null | undefined) {
  const e: Record<string, string> = editable || {};
  const out = { ...e };
  const tp = e["--tp"] || "#e8ecf4";
  const bs = e["--bs"] || "#141820";
  const ac = e["--ac"] || "#4a7cff";
  const pp = e["--pp"] || "#a78bfa";
  const gn = e["--gn"] || "#34d399";
  const am = e["--am"] || "#fbbf24";
  const rd = e["--rd"] || "#f87171";
  /* 135 §2(a) — THE BRAND, AND ITS DEFAULT IS THE ACCENT, NOT A LITERAL.
     `e["--brand"] || ac` and not `|| "#4a7cff"`: a firm that has never opened
     this picker must see the hue it already has, and a firm that has changed
     its accent must see THAT one. Falling back to the constant would give
     every re-themed firm a stranger's blue in its weekends and its shadows the
     moment this shipped, which is precisely the "change it did not ask for"
     the fence forbids.
     EMITTED below rather than only consumed, so the editor's picker can show
     the resolved value on a theme row that predates the token — the whole
     output map is written onto documentElement (`App.tsx:1881`). 203-glance-2
     §B: CSS reads it now — `.sidebar-brand h1` wears it (the firm's name, as
     every document's title does through deriveDocumentPalette below) — so
     `:root` carries its baseline for the unthemed first paint (App.css, beside
     `--ac`, the same figure). */
  const brand = e["--brand"] || ac;
  const c = lum(bs) > 0.5 ? _COEFFS_LIGHT_SURFACE : _COEFFS_DARK_SURFACE;
  out["--brand"] = brand;
  /* 195B §A [RULED — Laurent, 2026-09-19: "if none, fix it across app"] — THE
     ROW HOVER, LIFTED ON A DARK SURFACE, ONCE, FOR EVERY READER. His reasoning
     is the ruling's own: dark mode darkens uniformly, so a hover token measured
     invisible in dark is invisible in dark everywhere — one measurement covers
     every list. MEASURED (194E, 195 §E.6): `--bh` over the card `--bc` sits
     1.08 : 1 on the shipped dark theme (1.28 : 1 on the light default); 195
     lifted the two drawers alone, in CSS (`color-mix(in srgb, var(--bh) 90%,
     var(--tp))`, App.css `.drawerListRow`) to 1.43 : 1 and left every other
     list at 1.08 — two hover behaviours, born 2026-09-18.
     THE SAME MIX, IN THE TOKEN: the stored hover ground moved a tenth of the
     way toward the primary text, `_mixHex` being exactly `color-mix(in srgb)`'s
     arithmetic (a linear blend of the gamma-encoded channels; the drawers'
     1.43 : 1 reproduces to the hex). HERE and not in CSS because a custom
     property cannot reference itself — `--bh: color-mix(… var(--bh) …)` is a
     cycle, invalid at computed-value time — and because this map is what
     `App.tsx` writes onto documentElement, what both editor previews scope
     onto their sample, and what `auditContrast` reads: every reader of
     `var(--bh)` (eight CSS rules, some twenty inline hover handlers, the
     keyboard-highlighted menu row, the drag-over pane, the eraser's selected
     chip) moves at once and no surface is special. The day/night test is the
     one `--ts`/`--tm`/`--nwd`/`--shd`/`--st` already turn on; a light surface
     is untouched. STILL EDITABLE: the stored value is the base the admin
     picks; the editor seeds its buffer from the stored map and saves that
     buffer, so the lift never compounds. */
  const bh = e["--bh"] || "#1f2537";
  out["--bh"] = lum(bs) > 0.5 ? bh : _mixHex(bh, tp, 0.1);
  out["--ts"] = e["--ts"] || _mixHex(tp, bs, c.ts);
  out["--tm"] = e["--tm"] || _mixHex(tp, bs, c.tm);
  /* 111 §5d — stored value verbatim when the admin edited the row, else the
     parent accent: the jump follows the theme's amber, the link its purple. */
  out["--hlj"] = e["--hlj"] || am;
  out["--hll"] = e["--hll"] || pp;
  /* 132 §7 — THE NON-WORKING-DAY FIELD, and its whole point is the direction.
     RULED: "a weekend or observed holiday takes a DARKER background in day mode
     and a LIGHTER one in night mode". `lum(bs)` is the same day/night test the
     `--ts`/`--tm` coefficients two lines up already turn on, so the token is
     one name carrying the two values a theme needs — the pair, expressed the
     way `--ts`, `--tm` and `--as` express it, not two names of which only one
     could ever apply.
     ── 135 §2(d) — WITHDRAWN IN PLACE, 25 Aug 2026 ──────────────────────────
     What stood here was: "It moves the SURFACE toward black or white rather
     than tinting it with an accent: a weekend is not a state with a
     meaning-colour, it is a field, and a hue here would compete with every
     taxonomy pill drawn on top of it."

     That was good reasoning and Laurent has ruled otherwise — the pole is the
     BRAND now, not pure white or pure black. It is withdrawn here rather than
     deleted so nobody reads the code, recognises the old argument, and
     "restores" it as a fix. The competing-with-pills worry is answered by the
     arithmetic rather than dismissed: at `_NWD_BRAND_TINT` the pole is 55%
     brand, but the surface only travels 6% toward it, so what lands is a
     tinted field a few units off the surface — not a wash with a meaning.
     0.06 is unchanged and is still the reason: this paints behind a whole
     day's rows in the agenda and must read as a field, not as a selection. */
  out["--nwd"] =
    e["--nwd"] ||
    _mixHex(bs, _mixHex(lum(bs) > 0.5 ? "#000000" : "#ffffff", brand, _NWD_BRAND_TINT), 0.06);
  /* 133 §1 — THE DROP SHADOW, and what varies with the theme is its STRENGTH:
     0.6 over a pale card reads as a smudge, so a light theme gets 0.18.
     `lum(bs)` is the same day/night test `--ts`/`--tm`/`--nwd` already turn on,
     so one name carries the two values, exactly as `--nwd` does.
     NON-OVERRIDABLE, unlike `--nwd`: how strongly a firm marks its weekends is
     a taste and was asked for; how deep a dropdown's shadow is has not been,
     and a token nobody can reach is a token nobody can get wrong. It is also
     the only one of the two that COULD not be overridden — it resolves to
     `rgba`, and `themes.js` stores six-digit hex alone (see themeVars.mjs).

     ── 135 §2(d) — WITHDRAWN IN PLACE, 25 Aug 2026 ──────────────────────────
     What stood here was: "A shadow is black in both modes — it is the absence
     of light, not a colour with a meaning."

     Ruled otherwise: the shadow takes the brand's HUE and keeps its DARKNESS.
     The sentence is withdrawn rather than deleted for the same reason as
     `--nwd`'s above — it is exactly the argument somebody would reach for when
     "fixing" this line back. And it is only half-wrong: the darkness IS the
     part that matters, which is why `_SHADOW_BRAND_TINT` is 0.06 and not 0.22.
     See that constant for the measurement that fixes the ceiling. */
  out["--shd"] = _alphaShade(
    _mixHex("#000000", brand, _SHADOW_BRAND_TINT),
    lum(bs) > 0.5 ? 0.18 : 0.6,
  );
  /* 164 §A.7 — THE STATED COLOUR: a member's due typed by hand rather than
     derived from the round's formula renders in this, app-wide. Ice-blue on
     BOTH themes (164 glance correction: the original 35%-toward-black on a
     light surface landed on the text colour — #1e6383 against #387693 on
     staging's day theme, measured — and stopped reading as ice at all).
     The accent pulled toward WHITE on both surfaces: 55% on a dark one,
     10% on a light one — enough to ice it without losing the audit. At the
     default accent the audited ratios are 3.24 vs #ffffff / 3.02 vs
     #f5f7fa on the light side and 10.28 vs #141820 on the dark, against
     the HARD pairs' 3:1 bar (["--st","--bs"] below). Six-hex by
     construction, so it may be stored; overridable like --ts/--tm (see
     themeVars.mjs). */
  out["--st"] =
    e["--st"] || _mixHex(ac, "#ffffff", lum(bs) > 0.5 ? 0.1 : 0.55);
  out["--as"] = _alphaShade(ac, 0.12);
  out["--ag"] = _alphaShade(ac, 0.25);
  out["--ps"] = _alphaShade(pp, 0.12);
  out["--gs"] = _alphaShade(gn, 0.12);
  out["--ams"] = _alphaShade(am, 0.12);
  out["--rs"] = _alphaShade(rd, 0.12);
  return out;
}

// WCAG relative luminance (sRGB).
const _relLum = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (c: number) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

// Public hex → 0..1 luminance helper. Used by the contrast-anchor rule
// for --ts / --tm derivation in deriveThemeVars().
export function lum(hex: string) {
  const rgb = _hexToRgb(hex);
  if (!rgb) return 0;
  return _relLum(rgb);
}

export function contrastRatio(fgHex: string, bgHex: string) {
  const fg = _hexToRgb(fgHex);
  const bg = _hexToRgb(bgHex);
  if (!fg || !bg) return 0;
  const Lf = _relLum(fg);
  const Lb = _relLum(bg);
  const lighter = Math.max(Lf, Lb);
  const darker = Math.min(Lf, Lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/** The audit's bar: 3 : 1, AA-large. Named once — the document palette below picks its ink by it. */
const AUDIT_BAR = 3;

// Informational audit (M+3.6.1 — demoted from save-time gate). Both tiers
// are advisory: HARD lists text/accent-on-background pairs (severe — red
// in the panel) and SOFT lists chrome-on-chrome and status-on-raw pairs
// (advisory — amber). Chrome-on-chrome never has text rendered between
// it, so 3:1 isn't a meaningful threshold there. A future phase may
// re-introduce save gating keyed on subscription tier or theme-author role.
export function auditContrast(vars: Record<string, string> | null | undefined) {
  const v = deriveThemeVars(vars);
  const HARD = [
    // Body / secondary / muted text against the three surfaces text
    // typically renders on (raw, card, hover row).
    ["--tp", "--bs"],
    ["--tp", "--bc"],
    ["--tp", "--bh"],
    ["--ts", "--bs"],
    ["--ts", "--bc"],
    ["--tm", "--bs"],
    ["--tm", "--bc"],
    ["--tm", "--bh"],
    // Accent against surface — links, button labels, icon tints.
    ["--ac", "--bs"],
    // 164 §A.7 — the stated-amount text renders on the surface.
    ["--st", "--bs"],
  ];
  const SOFT = [
    // Background-vs-background pairs — chrome layered against chrome,
    // no text between them. Informational only.
    ["--bs", "--bd"],
    ["--bs", "--bc"],
    ["--bs", "--bh"],
    ["--bs", "--br"],
    // Status colours against raw surface — they typically render on
    // tinted pill backgrounds (--gs / --ams / --rs), not raw bs.
    ["--gn", "--bs"],
    ["--am", "--bs"],
    ["--rd", "--bs"],
  ];
  const eval1 = ([a, b]: string[]) => {
    const ratio = contrastRatio(v[a], v[b]);
    return {
      fg: a,
      bg: b,
      ratio: Math.round(ratio * 100) / 100,
      pass: ratio >= AUDIT_BAR,
    };
  };
  return {
    hardFails: HARD.map(eval1).filter((r) => !r.pass),
    softWarnings: SOFT.map(eval1).filter((r) => !r.pass),
    all: [...HARD, ...SOFT].map(eval1),
  };
}

// Treat a theme as "dark-leaning" iff its surface background's luminance is
// below 0.5. Used for the conditional `body.theme-dark` class in C6 (date
// picker indicator invert, etc.).
export function isDarkTheme(vars: Record<string, string> | null | undefined) {
  const bs = (vars && vars["--bs"]) || "#141820";
  const rgb = _hexToRgb(bs);
  if (!rgb) return true;
  return _relLum(rgb) < 0.5;
}

/* ── 203-glance §C.1 [RULED — Laurent, 2026-09-21: "didn't say firm's accent, said
 * firm's COLOUR, for titles, and accent maybe derived in the word rows and
 * wherever"] — THE DOCUMENT PALETTE, derived from the firm's DAY theme through
 * THIS file's own arithmetic, never a literal in the exporter ───────────────
 *
 * [JUDGEMENT — advisor, overrulable] THE DAY THEME, not the custom or night one:
 * a document is read on white paper or a white page, and a night-theme colour
 * is wrong there by construction. `dayThemeVarsOf` below resolves it off the
 * served rows (`FirmSettings.dayThemeId` → `Theme.vars`).
 *
 * THE VAR IS `--brand` — "Your firm's own colour" (VAR_SOURCE_EN above) — as
 * `deriveThemeVars` resolves it: the stored token when the row carries one, the
 * accent `--ac` when it predates the token (135 §2(a)). MEASURED on staging's
 * Theme rows, 2026-09-21: 12 of 13 carry no `--brand` (one, "WATT", does); the
 * pinned day themes ("Light", tenants 1 · 3 · 4) resolve to their `--ac`
 * #4a7cff, over `--bs` #ffffff and `--tp` #1f2937.
 *
 * THE INK over a brand-filled cell is DERIVED, never white typed a second time
 * (the brief's caution: white over a PALE firm colour is unreadable). The
 * theme's two text poles are its primary text `--tp` and its surface `--bs`,
 * ordered by luminance; the LIGHT pole is taken where it clears the contrast
 * audit's own bar over the fill (AUDIT_BAR, the HARD pairs' 3 : 1), the DARK
 * pole otherwise. Measured: the default blue #4a7cff takes the surface
 * (#ffffff on "Light", 3.74 : 1); amber #fbbf24 (1.67 : 1 against white),
 * green #34d399 (1.92) and red #f87171 (2.77) take the primary text. A
 * luminance threshold was refused: at `lum > 0.5` a green fill would take
 * white ink at 1.92 : 1.
 *
 * THE ROW STRIPE is `--nwd`'s arithmetic on the PAGE — the surface moved 6 %
 * toward a pole that is 55 % brand over black (`_NWD_BRAND_TINT`): the one
 * derivation in this file that paints a FIELD rather than a selection (132 §7,
 * 135 §2), which is what alternate rows are — with the page as the surface. A
 * document's page is white whatever the theme's `--bs`; `PAGE` is the paper,
 * a fact of the medium, the same white `--nwd` and `--st` already reach for.
 * On the default blue the stripe lands at #f2f4f8 — two units off the neutral
 * #F0F2F5 the Word rows wore before, now the firm's. */
const PAGE = "#ffffff";
export interface DocumentPalette {
  /** the firm's colour: the title, the header fill */
  brand: string;
  /** the ink over the header fill: the theme's light or dark text pole, by the audit's bar */
  ink: string;
  /** the alternate-row fill: the page tinted as `--nwd` tints a field */
  stripe: string;
}
export function deriveDocumentPalette(dayVars: Record<string, string> | null | undefined): DocumentPalette {
  const v = deriveThemeVars(dayVars);
  const brand = v["--brand"];
  // the poles as deriveThemeVars defaults them when a row carries neither
  const tp = v["--tp"] || varDefault("--tp");
  const bs = v["--bs"] || varDefault("--bs");
  const [dark, light] = lum(tp) <= lum(bs) ? [tp, bs] : [bs, tp];
  const ink = contrastRatio(light, brand) >= AUDIT_BAR ? light : dark;
  const stripe = _mixHex(PAGE, _mixHex("#000000", brand, _NWD_BRAND_TINT), 0.06);
  return { brand, ink, stripe };
}

/** 203-glance §C.1 — a view's `dayTheme` prop, narrowed at its seam (the views' props arrive as `any`) */
export const asThemeVars = (v: unknown): Record<string, string> | null =>
  typeof v === "object" && v !== null ? (v as Record<string, string>) : null;

/** 203-glance §C.1 — the firm's DAY theme's stored vars off the served rows (App
 *  holds `themes` and `firmSettings` untyped: narrowed here, once). Null until
 *  both have landed — the palette then derives from this file's defaults. */
export function dayThemeVarsOf(themes: unknown, firmSettings: unknown): Record<string, string> | null {
  const id = (firmSettings as { dayThemeId?: unknown } | null | undefined)?.dayThemeId;
  if (typeof id !== "number" || !Array.isArray(themes)) return null;
  const row = (themes as { id?: unknown; vars?: unknown }[]).find((x) => x.id === id);
  const vars = row?.vars;
  return vars !== null && typeof vars === "object" && !Array.isArray(vars) ? (vars as Record<string, string>) : null;
}
